import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Package, Users,
  Plus, X, AlertCircle, CheckCircle,
  Shuffle, Save, RefreshCw, FileText, Search,
  Trash2, Edit2, Eye, Filter
} from 'lucide-react';
import COEHeader from './COEHeader';
import logoutManager from '../logout';
import api from '../api';
import AcademicContextSelector from './AcademicContextSelector';

const PacketManager = () => {
  const location = useLocation();
  const [user] = useState(location.state?.user || null);
  const [tenant] = useState(location.state?.tenant || null);
  const [orgInfo] = useState(location.state?.orgInfo || { name: '', logoUrl: '' });
  const [academicContext, setAcademicContext] = useState(null);
  
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [sortField] = useState('USN');
  const [sortOrder] = useState('asc');
  const [evaluators,setEvaluators] = useState([]);
  const [packets, setPackets] = useState([]);
  const [existingPackets, setExistingPackets] = useState([]);
  const [evaluationRound, setEvaluationRound] = useState({ roundNumber: 1, roundType: 'first_evaluation', roundName: 'First Evaluation', assignmentId: null });
  const [packetSize, setPacketSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showDeletePacketModal, setShowDeletePacketModal] = useState(false);
  const [showEditPacketModal, setShowEditPacketModal] = useState(false);
  const [editingPacket, setEditingPacket] = useState(null);
  const [packetAssignmentDrafts, setPacketAssignmentDrafts] = useState({});
  const [selectedSavedPackets, setSelectedSavedPackets] = useState([]);
  const [bulkPacketEvaluatorId, setBulkPacketEvaluatorId] = useState('');
  const [packetSearch, setPacketSearch] = useState('');
  const [packetRoundFilter, setPacketRoundFilter] = useState('all');
  const [packetStatusFilter, setPacketStatusFilter] = useState('all');
  const [packetSortField, setPacketSortField] = useState('packetNumber');
  const [packetSortOrder, setPacketSortOrder] = useState('asc');
  const [selectedPacketDetails, setSelectedPacketDetails] = useState(null);
  const [showPacketDetailsModal, setShowPacketDetailsModal] = useState(false);
  const [packetProgress, setPacketProgress] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [draggedPacketKey, setDraggedPacketKey] = useState(null);
  const [packetDeleteTarget, setPacketDeleteTarget] = useState(null);
  const [packetEditStudents, setPacketEditStudents] = useState([]);
  const [packetEditEvaluatorId, setPacketEditEvaluatorId] = useState('');

  // Mobile navigation state
  const [activeMobileTab, setActiveMobileTab] = useState('students'); 

  const tenantId = localStorage.getItem('tenantId') || tenant?.id || tenant?._id;

  const showStatus = (type, message) => { 
    setStatusMessage({ type, message }); 
    setTimeout(() => setStatusMessage({ type: '', message: '' }), 5000); 
  };



  const isContextComplete = useCallback((ctx) => { 
    if (!ctx) return false; 
    if (!(ctx.programTypeId && ctx.programId && ctx.academicYearId)) return false; 
    if (ctx.evaluationMode === 'sem') return !!ctx.semId; 
    if (ctx.evaluationMode === 'term') return !!ctx.termId; 
    return false; 
  }, []);

  const fetchSubjects = useCallback(async (ctx) => {
    if (!tenantId || !isContextComplete(ctx)) return;
    setLoading(true);
    try {
      const params = { tenantId, programTypeId: ctx.programTypeId, programId: ctx.programId, academicYearId: ctx.academicYearId, evaluationMode: ctx.evaluationMode };
      if (ctx.evaluationMode === 'sem' && ctx.semId) params.semId = ctx.semId;
      else if (ctx.evaluationMode === 'term' && ctx.termId) params.termId = ctx.termId;
      const response = await api.get('/api/coe/end-exam-assignment/subjects', { params, headers: { 'x-tenant-id': tenantId } });
      if (response.data.success) {
        const finalizedSubjects = (response.data.subjects || []).filter(subject => subject.hasFinalizedTemplate === true);
        setSubjects(finalizedSubjects);
      }
    } catch (error) { console.error('Error fetching subjects:', error); showStatus('error', 'Failed to load course subjects.'); }
    finally { setLoading(false); }
  }, [tenantId, isContextComplete]);

  const fetchStudentsAndEvaluators = async (subjectId) => {
    setLoading(true);
    try {
      const studentParams = {
        tenantId,
        academicYearId: academicContext?.academicYearId,
        programTypeId: academicContext?.programTypeId,
        programId: academicContext?.programId
      };
      if (academicContext?.evaluationMode === 'sem' && academicContext?.semId) studentParams.semId = academicContext.semId;
      if (academicContext?.evaluationMode === 'term' && academicContext?.termId) studentParams.termId = academicContext.termId;

      console.log('[PacketManager] fetchStudentsAndEvaluators called', {
        subjectId,
        tenantId,
        academicContext,
        studentParams
      });

      const [studentsRes, evaluatorsRes, assignmentRes] = await Promise.all([
        api.get(`/api/coe/evaluators/subject/${subjectId}/students-for-packets`, { params: studentParams, headers: { 'x-tenant-id': tenantId } }),
        api.get('/api/coe/evaluators/subject-assignments/by-subject', { params: { subjectId, tenantId }, headers: { 'x-tenant-id': tenantId } }),
        api.get('/api/coe/end-exam-assignment/subject-assignments', { params: { subjectId, tenantId }, headers: { 'x-tenant-id': tenantId } })
      ]);

      console.log('[PacketManager] fetchStudentsAndEvaluators response', {
        studentsSuccess: studentsRes.data.success,
        studentsCount: studentsRes.data.students?.length || 0,
        existingPacketsCount: studentsRes.data.existingPackets?.length || 0,
        evaluatorAssignmentsCount: evaluatorsRes.data.assignments?.length || 0,
        endExamAssignmentsCount: assignmentRes.data.assignments?.length || 0,
        studentsMessage: studentsRes.data.message,
        assignmentIds: assignmentRes.data.assignments?.map(a => a._id)
      });

      if (studentsRes.data.success) { 
        setStudents(studentsRes.data.students || []); 
        setExistingPackets(studentsRes.data.existingPackets || []); 
      }
      if (evaluatorsRes.data.success) { 
        setEvaluators(evaluatorsRes.data.assignments || []); 
      }
      if (assignmentRes.data.assignments?.[0]) { 
        setEvaluationRound(prev => ({ ...prev, assignmentId: assignmentRes.data.assignments[0]._id })); 
      } else {
        showStatus('warning', 'Notice: An approved exam blueprint or question paper template was not found for this subject. Please set up the exam template first.');
      }
    } catch (error) { 
      console.error('Error fetching data:', error); 
      if (error.response?.status === 404) {
        setStudents([]);
        setEvaluators([]);
        setExistingPackets([]);
        setEvaluationRound(prev => ({ ...prev, assignmentId: null }));
        showStatus('warning', error.response?.data?.message || 'No approved exam paper format found for this subject.');
        return;
      }
      showStatus('error', 'Could not load student and evaluator information. Please try refreshing.'); 
    }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const nextDrafts = {};
    existingPackets.forEach((packet) => {
      const packetKey = packet.packetCode || packet._id;
      nextDrafts[packetKey] = packet.evaluatorId?._id || '';
    });
    setPacketAssignmentDrafts(nextDrafts);
    setSelectedSavedPackets(prev => prev.filter(packetKey => existingPackets.some(packet => (packet.packetCode || packet._id) === packetKey)));
  }, [existingPackets]);

  const handleContextChange = useCallback((ctx) => { 
    setAcademicContext(ctx); 
    if (isContextComplete(ctx)) fetchSubjects(ctx); 
  }, [fetchSubjects, isContextComplete]);

  const handleSubjectClick = async (subject) => { 
    setSelectedSubject(subject); 
    setPackets([]); 
    setSelectedStudents([]); 
    await fetchStudentsAndEvaluators(subject.subjectId); 
  };

  const filteredStudents = useMemo(() => {
    let filtered = [...students];
    if (studentSearch) { 
      const searchLower = studentSearch.toLowerCase(); 
      filtered = filtered.filter(s => s.studentName.toLowerCase().includes(searchLower) || (s.USN || '').toLowerCase().includes(searchLower)); 
    }
    filtered.sort((a, b) => { 
      let aVal = a[sortField] || '', bVal = b[sortField] || ''; 
      if (sortField === 'studentName') { aVal = a.studentName; bVal = b.studentName; } 
      if (sortOrder === 'asc') return aVal.localeCompare(bVal); 
      else return bVal.localeCompare(aVal); 
    });
    return filtered;
  }, [students, studentSearch, sortField, sortOrder]);

  const packetRoundOptions = useMemo(() => {
    const roundMap = new Map();
    existingPackets.forEach(packet => {
      const key = packet.evaluationRound?.roundType || 'unknown';
      if (!roundMap.has(key)) {
        roundMap.set(key, packet.evaluationRound?.roundName || packet.evaluationRound?.roundType || 'Evaluation');
      }
    });
    return Array.from(roundMap.entries()).map(([value, label]) => ({ value, label }));
  }, [existingPackets]);

  const visibleExistingPackets = useMemo(() => {
    const searchLower = packetSearch.trim().toLowerCase();
    let filtered = [...existingPackets];

    if (packetRoundFilter !== 'all') {
      filtered = filtered.filter(packet => (packet.evaluationRound?.roundType || 'unknown') === packetRoundFilter);
    }

    if (packetStatusFilter !== 'all') {
      filtered = filtered.filter(packet => {
        const status = packet.status?.status || 'pending';
        if (packetStatusFilter === 'assigned') return !!packet.evaluatorId;
        if (packetStatusFilter === 'unassigned') return !packet.evaluatorId;
        return status === packetStatusFilter;
      });
    }

    if (searchLower) {
      filtered = filtered.filter(packet => {
        const label = packet.packetDisplayLabel || packet.packetCode || '';
        const evaluatorName = packet.evaluatorId ? `${packet.evaluatorId.firstName || ''} ${packet.evaluatorId.lastName || ''}`.trim() : 'Unassigned';
        const roundLabel = packet.roundDisplayLabel || packet.evaluationRound?.roundName || '';
        return label.toLowerCase().includes(searchLower) || evaluatorName.toLowerCase().includes(searchLower) || roundLabel.toLowerCase().includes(searchLower) || (packet.subjectName || '').toLowerCase().includes(searchLower);
      });
    }

    filtered.sort((a, b) => {
      const aRound = a.evaluationRound?.roundNumber || 0;
      const bRound = b.evaluationRound?.roundNumber || 0;
      const aPacket = a.packetNumber || 0;
      const bPacket = b.packetNumber || 0;
      const aLabel = (a.packetDisplayLabel || a.packetCode || '').toString();
      const bLabel = (b.packetDisplayLabel || b.packetCode || '').toString();
      const aEval = (a.evaluatorId ? `${a.evaluatorId.firstName || ''} ${a.evaluatorId.lastName || ''}` : 'Unassigned').trim();
      const bEval = (b.evaluatorId ? `${b.evaluatorId.firstName || ''} ${b.evaluatorId.lastName || ''}` : 'Unassigned').trim();

      let comparison = 0;
      switch (packetSortField) {
        case 'round': comparison = aRound - bRound; break;
        case 'evaluator': comparison = aEval.localeCompare(bEval); break;
        case 'status': comparison = (a.status?.status || 'pending').localeCompare(b.status?.status || 'pending'); break;
        case 'label': comparison = aLabel.localeCompare(bLabel); break;
        case 'students': comparison = (a.studentIds?.length || 0) - (b.studentIds?.length || 0); break;
        default: comparison = aPacket - bPacket;
      }
      return packetSortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [existingPackets, packetSearch, packetRoundFilter, packetStatusFilter, packetSortField, packetSortOrder]);

  const studentLookup = useMemo(() => new Map(students.map(student => [student.studentId, student])), [students]);

  const draftPacketSummaries = useMemo(() => {
    return packets.map((packet) => {
      const packetStudentIds = packet.studentIds || [];
      const previewStudents = packetStudentIds
        .map(studentId => studentLookup.get(studentId))
        .filter(Boolean)
        .slice(0, 3)
        .map(student => student.studentName || student.USN || student.studentId);

      return {
        ...packet,
        totalStudents: packetStudentIds.length,
        previewStudents,
        packetLabel: `Packet ${packet.packetNumber}`
      };
    });
  }, [packets, studentLookup]);

  const draftPacketStats = useMemo(() => {
    return draftPacketSummaries.reduce((stats, packet) => {
      stats.totalStudents += packet.totalStudents || 0;
      if ((packet.studentIds || []).length > 0) stats.withStudents += 1;
      return stats;
    }, { totalPackets: draftPacketSummaries.length, totalStudents: 0, withStudents: 0 });
  }, [draftPacketSummaries]);

  const handleAutoDistribute = async () => {
    if (!selectedSubject) { showStatus('error', 'Please pick a subject first.'); return; }
    if (!evaluationRound.assignmentId) { showStatus('error', 'No exam setup has been approved for this course. Please finalize the exam setup first.'); return; }
    setLoading(true);
    try {
      const response = await api.post('/api/coe/evaluators/auto-distribute-packets', {
        tenantId, 
        subjectId: selectedSubject.subjectId, 
        packetSize,
        academicYearId: academicContext?.academicYearId,
        programTypeId: academicContext?.programTypeId,
        programId: academicContext?.programId,
        termId: academicContext?.termId || null,
        semId: academicContext?.semId || null,
        evaluationRound: { 
          ...evaluationRound, 
          assignmentId: evaluationRound.assignmentId, 
          programTypeId: academicContext.programTypeId, 
          programId: academicContext.programId, 
          academicYearId: academicContext.academicYearId 
        }
      }, { headers: { 'x-tenant-id': tenantId } });
      if (response.data.success) { 
        setPackets(response.data.packets); 
        showStatus('success', `${response.data.packets.length} bundles were automatically generated and are ready to be assigned.`); 
      }
    } catch (error) { 
      console.error('Error auto distributing:', error); 
      showStatus('error', error.response?.data?.message || 'Could not automatically group the student papers.'); 
    }
    finally { setLoading(false); }
  };

  const handleCreatePackets = async () => {
    if (!selectedSubject || packets.length === 0) { showStatus('error', 'There are no unsaved bundles to save.'); return; }
    if (!evaluationRound.assignmentId) { showStatus('error', 'Could not locate an approved question template layout.'); return; }
    setLoading(true);
    try {
      const response = await api.post('/api/coe/evaluators/create-packets', {
        tenantId, 
        subjectId: selectedSubject.subjectId, 
        assignmentId: evaluationRound.assignmentId, 
        packets,
        evaluationRound: { 
          roundNumber: evaluationRound.roundNumber, 
          roundType: evaluationRound.roundType, 
          roundName: evaluationRound.roundName 
        },
        programTypeId: academicContext.programTypeId, 
        programId: academicContext.programId,
        academicYearId: academicContext.academicYearId, 
        termId: academicContext.termId || null
      }, { headers: { 'x-tenant-id': tenantId } });
      if (response.data.success) { 
        showStatus('success', 'Your sheet bundles have been successfully created and locked in.'); 
        setShowCreateModal(false); 
        await fetchStudentsAndEvaluators(selectedSubject.subjectId); 
        setPackets([]); 
      }
    } catch (error) { 
      console.error('Error creating packets:', error); 
      showStatus('error', error.response?.data?.message || 'Failed to successfully save your paper bundles.'); 
    }
    finally { setLoading(false); }
  };

  const addPacket = () => { 
    if (selectedStudents.length === 0) { 
      showStatus('error', 'Please choose at least one student from the list first.'); 
      return; 
    }
    const nextPacketNumber = packets.length + 1;
    setPackets([...packets, { 
      evaluatorId: null, 
      evaluatorName: 'Not Assigned',
      studentIds: selectedStudents, 
      packetNumber: nextPacketNumber, 
      studentCount: selectedStudents.length 
    }]); 
    setSelectedStudents([]); 
    setShowCreateModal(true);
    showStatus('success', `Bundle Number ${nextPacketNumber} has been added into your temporary drafting room.`);
  };

  const removePacket = (index) => {
    setPackets(packets.filter((_, i) => i !== index));
    showStatus('success', 'Draft bundle removed.');
  };

  const toggleStudentSelection = (studentId) => { 
    setSelectedStudents(prev => prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]); 
  };

  const toggleSelectAll = () => { 
    if (selectedStudents.length === filteredStudents.length) setSelectedStudents([]); 
    else setSelectedStudents(filteredStudents.map(s => s.studentId)); 
  };

  const getExistingPacketInfo = (studentId) => { 
    const packet = existingPackets.find(p => p.studentIds?.includes(studentId)); 
    if (!packet) return null;
    const evaluatorName = packet.evaluatorId?.firstName ? `${packet.evaluatorId.firstName} ${packet.evaluatorId.lastName}` : 'No Teacher Assigned';
    return `In Bundle ${packet.packetNumber} — ${evaluatorName} — (${packet.status?.status || 'Pending'})`; 
  };

  const getEvaluatorDisplay = (evaluator) => {
    if (!evaluator) return 'No Teacher Assigned';
    if (evaluator.evaluatorId) return `${evaluator.evaluatorId.firstName} ${evaluator.evaluatorId.lastName}`;
    return `${evaluator.firstName} ${evaluator.lastName}`;
  };

  const getPacketIdentifier = useCallback((packet) => packet?.packetCode || packet?._id || null, []);

  const getPacketStudentIds = useCallback((packet) => (packet?.studentIds || []).map(studentId => String(studentId)), []);

  const getStudentAssignedPacketKey = useCallback((studentId, ignorePacketKey = null) => {
    const normalizedStudentId = String(studentId);
    const assignedPacket = existingPackets.find((packet) => {
      const packetKey = packet.packetCode || packet._id;
      if (ignorePacketKey && packetKey === ignorePacketKey) return false;
      return (packet.studentIds || []).some((packetStudentId) => String(packetStudentId) === normalizedStudentId);
    });
    return assignedPacket ? (assignedPacket.packetCode || assignedPacket._id) : null;
  }, [existingPackets]);

  const getPacketStudentPreview = useCallback((packet) => {
    const packetStudentIds = getPacketStudentIds(packet);
    return packetStudentIds.slice(0, 3).map((studentId) => {
      const student = students.find(item => String(item.studentId) === String(studentId));
      return student?.studentName || student?.USN || studentId;
    });
  }, [getPacketStudentIds, students]);

  const openPacketEditModal = (packet) => {
    setEditingPacket(packet);
    setPacketEditStudents(getPacketStudentIds(packet));
    setPacketEditEvaluatorId(packet.evaluatorId?._id || '');
    setShowEditPacketModal(true);
  };

  const openPacketDeleteModal = (packet) => {
    setPacketDeleteTarget(packet);
    setShowDeletePacketModal(true);
  };

  const handlePacketEditSave = async () => {
    if (!editingPacket) return;
    if (packetEditStudents.length === 0) {
      showStatus('warning', 'Please choose at least one student before saving this bundle.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.put('/api/coe/evaluators/packet', {
        packetId: editingPacket._id,
        packetCode: editingPacket.packetCode,
        studentIds: packetEditStudents,
        evaluatorId: packetEditEvaluatorId || editingPacket.evaluatorId?._id || null,
        reason: 'Packet edited by COE'
      }, { headers: { 'x-tenant-id': tenantId } });

      if (response.data.success) {
        showStatus('success', 'Bundle list updated successfully.');
        setShowEditPacketModal(false);
        setEditingPacket(null);
        setPacketEditStudents([]);
        setPacketEditEvaluatorId('');
        await fetchStudentsAndEvaluators(selectedSubject.subjectId);
      }
    } catch (error) {
      console.error('Error updating packet:', error);
      showStatus('error', error.response?.data?.message || 'Could not update this bundle card.');
    } finally {
      setLoading(false);
    }
  };

  const handlePacketDelete = async () => {
    if (!packetDeleteTarget) return;

    setLoading(true);
    try {
      const response = await api.delete('/api/coe/evaluators/packet', {
        data: {
          packetId: packetDeleteTarget._id,
          packetCode: packetDeleteTarget.packetCode
        },
        headers: { 'x-tenant-id': tenantId }
      });

      if (response.data.success) {
        showStatus('success', 'Bundle successfully deleted.');
        setShowDeletePacketModal(false);
        setPacketDeleteTarget(null);
        await fetchStudentsAndEvaluators(selectedSubject.subjectId);
      }
    } catch (error) {
      console.error('Error deleting packet:', error);
      showStatus('error', error.response?.data?.message || 'Could not delete this bundle.');
    } finally {
      setLoading(false);
    }
  };

  const handlePacketDragStart = (packet) => {
    setDraggedPacketKey(getPacketIdentifier(packet));
  };

  const handleEvaluatorDrop = async (evaluatorId) => {
    if (!draggedPacketKey) return;
    const packet = existingPackets.find(item => (item.packetCode || item._id) === draggedPacketKey);
    if (!packet) return;

    setDraggedPacketKey(null);
    await handlePacketAssignmentSave(packet, evaluatorId);
  };

  const toggleSavedPacketSelection = (packetKey) => {
    setSelectedSavedPackets(prev => prev.includes(packetKey) ? prev.filter(key => key !== packetKey) : [...prev, packetKey]);
  };

  const handleBulkPacketAssignment = async (evaluatorId) => {
    if (!selectedSavedPackets.length) {
      showStatus('warning', 'Please click on the checkboxes to select one or more bundles first.');
      return;
    }

    setLoading(true);
    try {
      const packetsToUpdate = existingPackets.filter(packet => selectedSavedPackets.includes(packet.packetCode || packet._id));
      const responses = await Promise.all(packetsToUpdate.map(packet => api.post('/api/coe/evaluators/reassign-packet', {
        packetId: packet._id,
        packetCode: packet.packetCode,
        newEvaluatorId: evaluatorId || null,
        reason: evaluatorId ? 'Bulk packet assignment by COE' : 'Bulk packet unassigned by COE'
      }, { headers: { 'x-tenant-id': tenantId } })));

      const allSucceeded = responses.every(response => response.data.success);
      if (allSucceeded) {
        showStatus('success', evaluatorId ? 'All selected paper bundles have been successfully assigned to the teacher.' : 'All selected assignments were cleared.');
        setSelectedSavedPackets([]);
        setBulkPacketEvaluatorId('');
        await fetchStudentsAndEvaluators(selectedSubject.subjectId);
      }
    } catch (error) {
      console.error('Error bulk updating packet assignments:', error);
      showStatus('error', error.response?.data?.message || 'Could not process the multi-packet assignment update.');
    } finally {
      setLoading(false);
    }
  };

  const handlePacketAssignmentSave = async (packet, evaluatorId) => {
    if (!packet) return;
    setLoading(true);
    try {
      const response = await api.post('/api/coe/evaluators/reassign-packet', {
        packetId: packet._id,
        packetCode: packet.packetCode,
        newEvaluatorId: evaluatorId || null,
        reason: evaluatorId ? 'Packet assignment updated by COE' : 'Packet unassigned by COE'
      }, { headers: { 'x-tenant-id': tenantId } });

      if (response.data.success) {
        showStatus('success', 'Teacher assignment saved successfully.');
        await fetchStudentsAndEvaluators(selectedSubject.subjectId);
      }
    } catch (error) {
      console.error('Error updating packet assignment:', error);
      showStatus('error', error.response?.data?.message || 'Failed to save the teacher mapping for this bundle.');
    } finally {
      setLoading(false);
    }
  };

  const openPacketDetails = async (packet) => {
    if (!packet?._id) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/coe/evaluators/packet/${packet._id}`, { headers: { 'x-tenant-id': tenantId } });
      if (response.data.success) {
        setSelectedPacketDetails(response.data.packet);
        setShowPacketDetailsModal(true);
      }
    } catch (error) {
      console.error('Error loading packet details:', error);
      showStatus('error', error.response?.data?.message || 'Failed to load details for this bundle.');
    } finally {
      setLoading(false);
    }
  };

  const openProgressModal = async () => {
    if (!selectedSubject) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/coe/evaluators/progress/${selectedSubject.subjectId}`, { headers: { 'x-tenant-id': tenantId } });
      if (response.data.success) {
        setPacketProgress(response.data);
        setShowProgressModal(true);
      }
    } catch (error) {
      console.error('Error loading packet progress:', error);
      showStatus('error', error.response?.data?.message || 'Could not fetch grading completion details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-slate-50 text-slate-900 antialiased font-sans">
      <COEHeader user={user} tenant={tenant} orgInfo={orgInfo} onLogout={logoutManager.logout} />
      
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Main Header Card */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 md:p-6 transition-all duration-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-50 rounded-xl text-purple-600 shadow-inner">
                <Package className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">Paper Bundle Manager</h1>
                <p className="text-xs md:text-sm text-slate-500 font-medium mt-0.5">Group student answer scripts into bundles and assign them to teachers for grading.</p>
              </div>
            </div>
            
            {selectedSubject && (
              <div className="flex items-center sm:self-center bg-purple-50/60 border border-purple-100 rounded-xl px-4 py-2 text-xs font-semibold text-purple-800">
                Selected Course: {selectedSubject.subjectCode}
              </div>
            )}
          </div>
        </div>

        {/* Academic Filters wrapper */}
        <div className="bg-white rounded-2xl border border-slate-200 p-1 shadow-sm">
          <AcademicContextSelector tenantId={tenantId} onContextChange={handleContextChange} initialContext={academicContext} />
        </div>

        {/* Global Notification Banner */}
        {statusMessage.message && (
          <div className={`rounded-xl border p-4 shadow-sm animate-in fade-in slide-in-from-top-4 duration-200 ${
            statusMessage.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 
            statusMessage.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 
            statusMessage.type === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <div className="flex items-start gap-3">
              {statusMessage.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
              <p className="text-sm font-medium leading-5">{statusMessage.message}</p>
            </div>
          </div>
        )}

        {/* Course Selection Grid */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/70">
            <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500">Available Subjects</h3>
          </div>
          <div className="p-5">
            {subjects.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No matching courses found for the selected department or year settings.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {subjects.map((subject) => (
                  <button 
                    key={subject.subjectId} 
                    onClick={() => handleSubjectClick(subject)} 
                    className={`p-4 rounded-xl border-2 text-left transition-all duration-150 relative overflow-hidden group ${
                      selectedSubject?.subjectId === subject.subjectId 
                        ? 'border-purple-600 bg-purple-50/40 ring-4 ring-purple-600/5' 
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
                    }`}
                  >
                    <p className="font-bold text-slate-800 group-hover:text-purple-900 transition-colors">{subject.subjectName}</p>
                    <p className="text-xs font-mono text-slate-400 group-hover:text-purple-600 mt-1.5">{subject.subjectCode}</p>
                    {selectedSubject?.subjectId === subject.subjectId && (
                      <span className="absolute top-0 right-0 w-3 h-3 bg-purple-600 rounded-bl-lg" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Workspace Panels */}
        {selectedSubject && (
          <div className="space-y-6">
            
            {/* Mobile Tab Toggle Toggles */}
            <div className="block lg:hidden bg-slate-200/70 p-1 rounded-xl grid grid-cols-2 gap-1">
              <button 
                onClick={() => setActiveMobileTab('students')}
                className={`py-2.5 text-xs font-bold rounded-lg transition-all ${activeMobileTab === 'students' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              >
                Students List ({students.length})
              </button>
              <button 
                onClick={() => setActiveMobileTab('packets')}
                className={`py-2.5 text-xs font-bold rounded-lg transition-all ${activeMobileTab === 'packets' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              >
                Saved Bundles ({existingPackets.length})
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              
              {/* Left Column - Student Checklist */}
              <div className={`bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden lg:col-span-5 ${activeMobileTab === 'students' ? 'block' : 'hidden lg:block'}`}>
                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/40 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <h3 className="text-sm font-bold text-slate-700">Available Students ({students.length})</h3>
                    </div>
                  </div>
                  
                  {/* Search and Quick Action Controls */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <input 
                        type="text" 
                        placeholder="Search student name or roll number..." 
                        value={studentSearch} 
                        onChange={(e) => setStudentSearch(e.target.value)} 
                        className="pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs w-full focus:outline-none focus:border-purple-500 bg-white font-medium" 
                      />
                    </div>
                    <div className="flex items-center gap-2 justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] font-semibold text-slate-400">Bundle Size:</span>
                        <input 
                          type="number" 
                          value={packetSize} 
                          onChange={(e) => setPacketSize(parseInt(e.target.value) || 0)} 
                          className="w-14 px-2 py-1 border border-slate-200 rounded-md text-xs font-bold text-center focus:outline-none focus:border-purple-500" 
                        />
                      </div>
                      <button 
                        onClick={handleAutoDistribute} 
                        disabled={loading} 
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white font-bold rounded-lg text-xs transition-colors shadow-sm"
                      >
                        <Shuffle className="w-3 h-3" /> Auto Group
                      </button>
                      <button 
                        onClick={addPacket} 
                        disabled={selectedStudents.length === 0} 
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white font-bold rounded-lg text-xs transition-colors shadow-sm"
                      >
                        <Plus className="w-3 h-3" /> Create Bundle
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-xs">
                    <button onClick={toggleSelectAll} className="text-indigo-600 hover:text-indigo-800 font-bold transition-colors">
                      {selectedStudents.length === filteredStudents.length && filteredStudents.length > 0 ? 'Clear Selection' : 'Select All Filtered'}
                    </button>
                    <span className="text-slate-400 font-medium">{selectedStudents.length} chosen</span>
                  </div>
                </div>

                {/* Mobile Responsive Student List */}
                <div className="p-2 max-h-[32rem] overflow-y-auto divide-y divide-slate-100">
                  {filteredStudents.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-8">No matching students found.</p>
                  ) : (
                    filteredStudents.map((student, idx) => {
                      const isSelected = selectedStudents.includes(student.studentId);
                      const hasPacket = getExistingPacketInfo(student.studentId);
                      return (
                        <div 
                          key={student.studentId} 
                          onClick={() => toggleStudentSelection(student.studentId)}
                          className={`p-3 rounded-xl flex items-center justify-between gap-3 cursor-pointer transition-colors ${
                            isSelected ? 'bg-purple-50/40' : 'hover:bg-slate-50/70'
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <input 
                              type="checkbox" 
                              checked={isSelected} 
                              onChange={() => {}} 
                              className="w-4 h-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500 flex-shrink-0" 
                            />
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-slate-800 truncate">{student.studentName}</p>
                              <p className="text-[11px] font-mono font-semibold text-slate-400 mt-0.5">{student.USN || 'No Roll Number'}</p>
                              {hasPacket && (
                                <span className="inline-block mt-1 text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium border border-amber-100 truncate max-w-full">
                                  {hasPacket}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-[10px] text-slate-400 font-mono">#{idx + 1}</span>
                        </div>
                      );
                    })
                  )}
                </div>
                
                <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                  <p className="text-[11px] text-slate-400 leading-relaxed font-medium">
                    Tip: Selected students will be placed into a draft bundle. Click "Create Bundle" to edit and review your draft before saving.
                  </p>
                </div>
              </div>

              {/* Right Column - Existing Saved Bundles Dashboard */}
              <div className={`bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden lg:col-span-7 ${activeMobileTab === 'packets' ? 'block' : 'hidden lg:block'}`}>
                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/40 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-slate-400" />
                      <h3 className="text-sm font-bold text-slate-700">
                        Active Paper Bundles ({visibleExistingPackets.length}/{existingPackets.length})
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                      <button 
                        onClick={openProgressModal} 
                        disabled={!selectedSubject} 
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold rounded-xl text-xs disabled:opacity-40 transition-colors shadow-sm"
                      >
                        <RefreshCw className="w-3 h-3" /> Check Live Progress
                      </button>
                      <button 
                        onClick={() => setShowCreateModal(true)} 
                        disabled={packets.length === 0} 
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs disabled:opacity-40 transition-colors shadow-sm"
                      >
                        <Save className="w-3 h-3" /> Save All Bundles
                      </button>
                    </div>
                  </div>

                  {/* Filter and Sorting Actions Bar */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <input
                        type="text"
                        value={packetSearch}
                        onChange={(e) => setPacketSearch(e.target.value)}
                        placeholder="Search bundle names or teachers..."
                        className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-purple-500"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-1">
                      <select value={packetRoundFilter} onChange={(e) => setPacketRoundFilter(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none font-medium text-slate-600 truncate">
                        <option value="all">All Rounds</option>
                        {packetRoundOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                      <select value={packetStatusFilter} onChange={(e) => setPacketStatusFilter(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none font-medium text-slate-600 truncate">
                        <option value="all">Statuses</option>
                        <option value="assigned">Assigned</option>
                        <option value="unassigned">Unassigned</option>
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                      <select value={packetSortField} onChange={(e) => setPacketSortField(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none font-medium text-slate-600 truncate">
                        <option value="packetNumber">Order By No.</option>
                        <option value="label">Order By Label</option>
                        <option value="round">Order By Round</option>
                        <option value="evaluator">Order By Teacher</option>
                        <option value="status">Order By Status</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-[11px] text-slate-400 font-medium">
                    <button 
                      onClick={() => setPacketSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')} 
                      className="px-2 py-1 rounded bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors font-semibold"
                    >
                      Sort order: {packetSortOrder === 'asc' ? 'A to Z' : 'Z to A'}
                    </button>
                    <span>Label preview: <strong className="font-mono text-slate-600">{selectedSubject?.subjectCode || 'PKT'}-FRS-01</strong></span>
                  </div>
                </div>

                <div className="p-4 space-y-4 max-h-[38rem] overflow-y-auto">
                  
                  {/* Draft Workspace Widget Box Banner */}
                  <div className="rounded-xl border border-dashed border-purple-200 bg-purple-50/20 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold text-purple-900">Current Work-in-Progress Room</p>
                        <p className="text-[11px] text-purple-700/80 mt-0.5">Your newly grouped bundles are saved safely here until you hit save changes.</p>
                      </div>
                      <div className="grid grid-cols-3 gap-1 text-center font-mono text-[10px] font-bold">
                        <div className="bg-white border rounded-lg px-2 py-1"><p className="text-slate-400 scale-90">BUNDLES</p><p className="text-slate-800">{draftPacketStats.totalPackets}</p></div>
                        <div className="bg-white border rounded-lg px-2 py-1"><p className="text-slate-400 scale-90">STUDENTS</p><p className="text-slate-800">{draftPacketStats.totalStudents}</p></div>
                        <div className="bg-white border rounded-lg px-2 py-1"><p className="text-slate-400 scale-90">READY</p><p className="text-emerald-600">{draftPacketStats.withStudents}</p></div>
                      </div>
                    </div>
                    {packets.length > 0 && (
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="w-full mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg border border-purple-200 bg-white px-3 py-2 text-xs font-bold text-purple-700 hover:bg-purple-50 transition-colors shadow-sm"
                      >
                        <FileText className="w-3.5 h-3.5" /> Open Review Window for New Bundles ({packets.length})
                      </button>
                    )}
                  </div>

                  {existingPackets.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <p className="text-sm">No saved bundles have been created for this course yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-4 pt-2">
                      
                      {/* Bulk Settings Container Box Component */}
                      <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-3 space-y-3">
                        <div className="flex items-center gap-2">
                          <Filter className="w-3.5 h-3.5 text-slate-400" />
                          <h4 className="text-xs font-bold text-slate-600">Assign Multiple Bundles at Once</h4>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <select
                            value={bulkPacketEvaluatorId}
                            onChange={(e) => setBulkPacketEvaluatorId(e.target.value)}
                            className="flex-1 px-2 py-1.5 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none text-slate-700 font-medium"
                          >
                            <option value="">Select a teacher to assign to...</option>
                            {evaluators.map(e => (
                              <option key={e._id} value={e.evaluatorId?._id || e._id}>{getEvaluatorDisplay(e)}</option>
                            ))}
                          </select>
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => handleBulkPacketAssignment(bulkPacketEvaluatorId)}
                              disabled={loading || selectedSavedPackets.length === 0}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-lg text-xs font-bold transition-colors shadow-sm"
                            >
                              Assign Selected
                            </button>
                            <button
                              onClick={() => handleBulkPacketAssignment('')}
                              disabled={loading || selectedSavedPackets.length === 0}
                              className="px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-bold transition-colors shadow-sm"
                            >
                              Clear Assignments
                            </button>
                          </div>
                        </div>
                        
                        {/* Drag and Drop Sandbox Zone */}
                        <div
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={() => handleEvaluatorDrop('')}
                          className="hidden sm:block rounded-lg border border-dashed border-slate-300 bg-white/50 px-3 py-2.5 text-center text-[11px] text-slate-400 font-medium hover:border-red-400 hover:bg-red-50/10 transition-all"
                        >
                          Computer drag & drop bar: Drop a bundle box here to unassign it instantly.
                        </div>

                        {/* Interactive Drag and Drop Horizontal Teacher Block Map Row */}
                        <div className="overflow-x-auto pb-1 flex gap-2">
                          {evaluators.map((evaluator) => {
                            const evaluatorId = evaluator.evaluatorId?._id || evaluator._id;
                            const packetCount = existingPackets.filter(packet => (packet.evaluatorId?._id || packet.evaluatorId) && String(packet.evaluatorId._id || packet.evaluatorId) === String(evaluatorId)).length;
                            return (
                              <div
                                key={evaluatorId}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={() => handleEvaluatorDrop(evaluatorId)}
                                className="flex-shrink-0 min-w-[140px] max-w-[180px] rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] transition-all hover:border-purple-300 hover:bg-purple-50/50 font-medium"
                              >
                                <p className="font-bold text-slate-700 truncate">{getEvaluatorDisplay(evaluator)}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5 font-medium">{packetCount} bundles linked</p>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-slate-400 font-medium pl-1">{selectedSavedPackets.length} boxes selected for modification</p>
                      </div>

                      {/* Main Saved Bundles Loop Items rows */}
                      <div className="space-y-2">
                        {visibleExistingPackets.map((packet) => {
                          const packetKey = packet.packetCode || packet._id;
                          const currentEvaluatorId = packet.evaluatorId?._id || '';
                          const draftEvaluatorId = packetAssignmentDrafts[packetKey] ?? currentEvaluatorId;
                          const currentEvaluatorName = packet.evaluatorId ? `${packet.evaluatorId.firstName} ${packet.evaluatorId.lastName || ''}`.trim() : 'Unassigned';
                          const packetLabel = packet.packetDisplayLabel || packet.packetCode || `Packet ${packet.packetNumber}`;
                          const roundLabel = packet.roundDisplayLabel || packet.evaluationRound?.roundName || packet.evaluationRound?.roundType || 'Evaluation';
                          const packetStudentPreview = getPacketStudentPreview(packet);

                          return (
                            <div
                              key={packetKey}
                              draggable
                              onDragStart={() => handlePacketDragStart(packet)}
                              onDragEnd={() => setDraggedPacketKey(null)}
                              className="rounded-xl border border-slate-200 bg-white p-4 transition-all hover:shadow-md hover:border-slate-300 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-grab active:cursor-grabbing"
                            >
                              <div className="flex items-start gap-3 min-w-0 flex-1">
                                <input
                                  type="checkbox"
                                  checked={selectedSavedPackets.includes(packetKey)}
                                  onChange={() => toggleSavedPacketSelection(packetKey)}
                                  className="mt-1 w-4 h-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500 flex-shrink-0"
                                />
                                <div className="min-w-0 flex-1 space-y-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <p className="text-sm font-bold text-slate-800 truncate">{packetLabel}</p>
                                    <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-600">{roundLabel}</span>
                                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${packet.evaluatorId ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                                      {packet.assignmentStateLabel || (packet.evaluatorId ? 'Assigned' : 'Unassigned')}
                                    </span>
                                  </div>
                                  <p className="text-xs font-mono font-medium text-slate-400 truncate">Code: {packet.packetCode || 'No Barcode Added'}</p>
                                  <div className="text-xs text-slate-600 font-medium">
                                    Assigned Teacher: <span className={currentEvaluatorId ? 'text-emerald-700 font-bold' : 'text-amber-700 font-bold'}>{currentEvaluatorName}</span>
                                  </div>
                                  <p className="text-[11px] text-slate-400 font-semibold">
                                    Total Papers inside: {packet.studentIds?.length || packet.totalStudents || 0} answer sheets · Grading Status: {packet.status?.status || 'Pending'}
                                  </p>
                                  <p className="text-[10px] font-medium text-slate-400 bg-slate-50 border border-slate-100 p-1.5 rounded truncate">
                                    Contains papers for: {packetStudentPreview.length > 0 ? packetStudentPreview.join(', ') : 'No preview layout strings loaded.'}
                                  </p>
                                </div>
                              </div>

                              {/* Manual Teacher Row Allocation Dropdowns */}
                              <div className="border-t md:border-t-0 pt-3 md:pt-0 flex flex-col sm:flex-row md:flex-col gap-2 min-w-full sm:min-w-0 md:min-w-[160px]">
                                <select
                                  value={draftEvaluatorId}
                                  onChange={(e) => setPacketAssignmentDrafts(prev => ({ ...prev, [packetKey]: e.target.value }))}
                                  className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none text-slate-700 font-medium"
                                >
                                  <option value="">Unassigned</option>
                                  {evaluators.map(e => (
                                    <option key={e._id} value={e.evaluatorId?._id || e._id}>{getEvaluatorDisplay(e)}</option>
                                  ))}
                                </select>
                                <div className="grid grid-cols-2 gap-1">
                                  <button
                                    onClick={() => handlePacketAssignmentSave(packet, draftEvaluatorId)}
                                    disabled={loading}
                                    className="px-2 py-1.5 bg-purple-600 text-white font-bold rounded-lg text-[11px] hover:bg-purple-700 shadow-sm transition-colors disabled:opacity-40"
                                  >
                                    Apply
                                  </button>
                                  <button
                                    onClick={() => handlePacketAssignmentSave(packet, '')}
                                    disabled={loading || !currentEvaluatorId}
                                    className="px-2 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 font-semibold rounded-lg text-[11px] shadow-sm transition-colors disabled:opacity-40"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <div className="grid grid-cols-3 gap-1">
                                  <button
                                    onClick={() => openPacketDetails(packet)}
                                    className="p-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg text-xs flex justify-center items-center transition-colors"
                                    title="View papers inside bundle"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => openPacketEditModal(packet)}
                                    className="p-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg text-xs flex justify-center items-center transition-colors"
                                    title="Add or remove students from bundle"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => openPacketDeleteModal(packet)}
                                    className="p-1.5 border border-red-200 bg-red-50/20 hover:bg-red-50 text-red-600 rounded-lg text-xs flex justify-center items-center transition-colors"
                                    title="Delete this bundle box configuration completely"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}
      </main>

      {/* POPUP MODAL SCREEN OVERLAYS */}

      {/* Modal 1: Individual Bundle Papers Content Listing */}
      {showPacketDetailsModal && selectedPacketDetails && (
        <div className="fixed inset-0 z-[1000] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-250">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] sm:max-h-[85vh] overflow-hidden flex flex-col border border-slate-200 animate-in slide-in-from-bottom-8 duration-200">
            <div className="border-b border-slate-100 px-5 py-4 flex items-center justify-between bg-slate-50/50">
              <div>
                <h2 className="text-base font-bold text-slate-800">
                  Detailed papers view: {selectedPacketDetails.packetDisplayLabel || selectedPacketDetails.packetCode || `Packet ${selectedPacketDetails.packetNumber}`}
                </h2>
                <p className="text-[11px] text-slate-400 font-medium mt-0.5">Exam cycle: {selectedPacketDetails.roundDisplayLabel || selectedPacketDetails.evaluationRound?.roundName || 'Evaluation'}</p>
              </div>
              <button onClick={() => setShowPacketDetailsModal(false)} className="p-1 rounded-full hover:bg-slate-200 text-slate-400 transition-colors"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="p-5 overflow-y-auto space-y-5 flex-1">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-center"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total Scripts Inside</p><p className="text-xl font-bold font-mono text-slate-800 mt-1">{selectedPacketDetails.totalStudents || selectedPacketDetails.students?.length || 0}</p></div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-center"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Grading Status</p><p className="text-sm font-bold text-slate-700 mt-2 truncate px-1">{selectedPacketDetails.status?.status || 'pending'}</p></div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-center col-span-2 sm:col-span-1"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Assigned Staff Teacher</p><p className="text-xs font-bold text-slate-700 mt-2 truncate px-1">{selectedPacketDetails.evaluatorId ? `${selectedPacketDetails.evaluatorId.firstName || ''} ${selectedPacketDetails.evaluatorId.lastName || ''}`.trim() : 'Unassigned'}</p></div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-center"><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Completion Score</p><p className="text-xl font-bold font-mono text-purple-700 mt-1">{selectedPacketDetails.percentage ? `${Math.round(selectedPacketDetails.percentage)}%` : '0%'}</p></div>
              </div>

              <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-inner">
                <div className="px-4 py-2.5 bg-slate-50/80 border-b border-slate-200 font-bold text-xs text-slate-500">
                  List of student answer sheets placed inside this box
                </div>
                <div className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
                  {(selectedPacketDetails.students || []).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-6">No matching student sheet references discovered inside this bundle block card container wrapper.</p>
                  ) : (
                    (selectedPacketDetails.students || []).map((student) => (
                      <div key={student.studentId} className="px-4 py-2.5 flex items-center justify-between gap-3 text-xs">
                        <div className="min-w-0">
                          <p className="font-bold text-slate-800 truncate">{student.studentName}</p>
                          <p className="text-[10px] font-mono font-medium text-slate-400 mt-0.5">Roll No: {student.USN || student.rollNumber || 'N/A'}</p>
                        </div>
                        <div className="font-mono bg-slate-100 border text-slate-700 px-2 py-0.5 rounded text-[10px] font-bold flex-shrink-0">
                          Marks Given: {student.marksObtained || 0}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 px-5 py-3 bg-slate-50 flex justify-end"><button onClick={() => setShowPacketDetailsModal(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-xs font-bold transition-colors">Dismiss</button></div>
          </div>
        </div>
      )}

      {/* Modal 2: Course Wide Completion Analysis Progress Matrix Board */}
      {showProgressModal && packetProgress && (
        <div className="fixed inset-0 z-[998] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-250">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] sm:max-h-[85vh] overflow-hidden flex flex-col border border-slate-200 animate-in slide-in-from-bottom-8 duration-200">
            <div className="border-b border-slate-100 px-6 py-4 flex items-center justify-between bg-slate-50/50">
              <div>
                <h2 className="text-base font-bold text-slate-800">Overall Grading Progress Statistics Dashboard</h2>
                <p className="text-xs text-slate-400 font-medium mt-0.5">Subject course: {selectedSubject?.subjectName}</p>
              </div>
              <button onClick={() => setShowProgressModal(false)} className="p-1 rounded-full hover:bg-slate-200 text-slate-400 transition-colors"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-6 flex-1 bg-slate-50/30">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-white border border-slate-200 p-4 rounded-xl shadow-inner">
                <div className="text-center border-r last:border-r-0 border-slate-100"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Active Exam Rounds</p><p className="text-2xl font-bold font-mono text-slate-800 mt-1">{packetProgress.overallSummary?.totalRounds || 0}</p></div>
                <div className="text-center border-r last:border-r-0 border-slate-100"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Paper Bundles</p><p className="text-2xl font-bold font-mono text-slate-800 mt-1">{packetProgress.overallSummary?.totalPackets || 0}</p></div>
                <div className="text-center border-r last:border-r-0 border-slate-100"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Fully Evaluated Bundles</p><p className="text-2xl font-bold font-mono text-emerald-600 mt-1">{packetProgress.overallSummary?.totalCompleted || 0}</p></div>
                <div className="text-center"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Course Completion Rate</p><p className="text-2xl font-bold font-mono text-purple-700 mt-1">{Math.round(packetProgress.overallSummary?.overallPercentage || 0)}%</p></div>
              </div>

              <div className="space-y-4">
                {(packetProgress.rounds || []).map((round) => (
                  <div key={`${round.roundNumber}-${round.roundType}`} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-slate-700">Exam Stage Name: {round.roundName || round.roundType}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">{round.summary?.totalPackets || 0} bundles created · {round.summary?.overallPercentage ? `${Math.round(round.summary.overallPercentage)}%` : '0%'} finished grading by staff.</p>
                      </div>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {(round.packets || []).map((packet) => (
                        <div key={packet.packetId} className="px-4 py-3 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors">
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-800">{packet.packetDisplayLabel || packet.packetCode || `Packet ${packet.packetNumber}`}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5 font-medium">Assigned Teacher: {packet.evaluatorName || 'None Assigned'} · Status: {packet.status || 'pending'} · Papers count: {packet.totalStudents || 0} students sheets</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs font-mono font-bold text-slate-800">{Math.round(packet.percentage || 0)}%</p>
                            <button onClick={() => openPacketDetails({ _id: packet.packetId })} className="text-[10px] text-indigo-600 hover:text-indigo-800 font-bold tracking-tight">Expand List</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-slate-100 px-6 py-3 bg-slate-50 flex justify-end"><button onClick={() => setShowProgressModal(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-xs font-bold transition-colors">Close View</button></div>
          </div>
        </div>
      )}

      {/* Modal 3: Add/Remove Students and Edit Bundle Configuration Panel */}
      {showEditPacketModal && editingPacket && (
        <div className="fixed inset-0 z-[1001] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-250">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] sm:max-h-[88vh] overflow-hidden flex flex-col border border-slate-200 animate-in slide-in-from-bottom-8 duration-200">
            <div className="border-b border-slate-100 px-6 py-4 flex items-center justify-between bg-slate-50/50">
              <div>
                <h2 className="text-base font-bold text-slate-800">Edit Bundle Student Membership and Teacher Assignment Settings</h2>
                <p className="text-[11px] text-slate-400 font-medium mt-0.5">Check or uncheck boxes to modify which papers go inside this specific bundle box package row.</p>
              </div>
              <button onClick={() => setShowEditPacketModal(false)} className="p-1 rounded-full hover:bg-slate-200 text-slate-400 transition-colors"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-4 flex-1 bg-slate-50/20">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-xl border border-slate-100 bg-white p-2.5 text-center"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Active Box</p><p className="text-xs font-bold text-slate-700 truncate mt-1">{editingPacket.packetDisplayLabel || editingPacket.packetCode || `Packet ${editingPacket.packetNumber}`}</p></div>
                <div className="rounded-xl border border-slate-100 bg-white p-2.5 text-center"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Original Size</p><p className="text-sm font-bold font-mono text-slate-700 mt-1">{editingPacket.studentIds?.length || 0}</p></div>
                <div className="rounded-xl border border-slate-100 bg-white p-2.5 text-center"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">New Size</p><p className="text-sm font-bold font-mono text-purple-700 mt-1">{packetEditStudents.length}</p></div>
                <div className="rounded-xl border border-slate-100 bg-white p-2.5 text-center"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Exam System Mode</p><p className="text-xs font-bold text-slate-600 truncate mt-1.5">{academicContext?.evaluationMode || 'N/A'}</p></div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 bg-white shadow-sm space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">Select or change the assigned grading teacher</label>
                <select value={packetEditEvaluatorId} onChange={(e) => setPacketEditEvaluatorId(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white font-medium text-slate-700 focus:outline-none focus:border-purple-500">
                  <option value="">No Teacher (Unassigned)</option>
                  {evaluators.map(evaluator => (
                    <option key={evaluator._id} value={evaluator.evaluatorId?._id || evaluator._id}>{getEvaluatorDisplay(evaluator)}</option>
                  ))}
                </select>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col">
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-xs">
                  <span className="font-bold text-slate-600">Student Papers Registry Directory</span>
                  <span className="text-[10px] text-slate-400 font-semibold">Locked lines mean that paper is already in another bundle</span>
                </div>
                
                <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                  {filteredStudents.map((student, index) => {
                    const studentId = student.studentId;
                    const assignedPacketKey = getStudentAssignedPacketKey(studentId, getPacketIdentifier(editingPacket));
                    const isSelected = packetEditStudents.includes(String(studentId));
                    const canSelect = !assignedPacketKey || assignedPacketKey === getPacketIdentifier(editingPacket);

                    return (
                      <label key={studentId} className={`flex items-center justify-between gap-3 px-4 py-2.5 text-xs ${canSelect ? 'cursor-pointer hover:bg-slate-50/70' : 'bg-slate-50/80'}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={!canSelect}
                            onChange={() => setPacketEditStudents(prev => prev.includes(String(studentId)) ? prev.filter(id => id !== String(studentId)) : [...prev, String(studentId)])}
                            className="w-4 h-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500 flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <p className="font-bold text-slate-800 truncate">{student.studentName}</p>
                            <p className="text-[10px] font-mono font-medium text-slate-400 mt-0.5">Roll number: {student.USN || 'N/A'} · Item index position: {index + 1}</p>
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold ${assignedPacketKey && assignedPacketKey !== getPacketIdentifier(editingPacket) ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {assignedPacketKey && assignedPacketKey !== getPacketIdentifier(editingPacket) ? 'Locked in other box' : 'Available for choice'}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 px-6 py-3.5 bg-slate-50 flex items-center justify-end gap-2">
              <button onClick={() => setShowEditPacketModal(false)} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 font-bold text-xs hover:bg-slate-50 transition-colors">Cancel changes</button>
              <button onClick={handlePacketEditSave} disabled={loading} className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shadow-sm transition-colors disabled:opacity-40">{loading ? 'Saving details...' : 'Confirm and Apply Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 4: Delete Confirmation Dialog */}
      {showDeletePacketModal && packetDeleteTarget && (
        <div className="fixed inset-0 z-[1002] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-150">
            <div className="border-b border-slate-100 px-5 py-4 bg-red-50/50 flex justify-between items-center">
              <h2 className="text-sm font-bold text-red-900">Are you sure you want to delete this bundle?</h2>
              <button onClick={() => setShowDeletePacketModal(false)} className="p-1 rounded-full text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-slate-600 leading-relaxed font-semibold">
                This will delete the bundle labelled: <strong className="text-slate-900 font-bold font-mono">{packetDeleteTarget.packetDisplayLabel || packetDeleteTarget.packetCode || `Packet ${packetDeleteTarget.packetNumber}`}</strong>.
              </p>
              <p className="bg-red-50 text-red-700 border border-red-100 text-[11px] p-2.5 rounded-lg font-medium">
                Warning: Deleting this bundle will unpack all {packetDeleteTarget.studentIds?.length || 0} student answer sheets inside it, making them unassigned once more. Teacher links will also be removed.
              </p>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setShowDeletePacketModal(false)} className="px-3.5 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-xs font-bold hover:bg-slate-50">No, Cancel</button>
                <button onClick={handlePacketDelete} disabled={loading} className="px-3.5 py-2 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700 shadow-sm transition-all">{loading ? 'Deleting bundle...' : 'Yes, Delete Bundle box permanently'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal 5: Review Draft Staging Console sheet overlay */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[999] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-250">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] sm:max-h-[90vh] overflow-hidden flex flex-col border border-slate-200 animate-in slide-in-from-bottom-8 duration-200">
            <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center bg-slate-50/50">
              <div>
                <h2 className="text-base font-bold text-slate-800">Review New Bundles Staging Room Dashboard</h2>
                <p className="text-[11px] text-slate-400 font-medium mt-0.5">Please double-check your custom configurations layout stacks here before confirming.</p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="p-1 rounded-full hover:bg-slate-200 text-slate-400 transition-colors"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-6 flex-1 bg-slate-50/20">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-inner text-center"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Memory Blocks</p><p className="text-xl font-bold font-mono text-slate-800 mt-0.5">{draftPacketStats.totalPackets}</p></div>
                <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-inner text-center"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Compiled Arrays</p><p className="text-xl font-bold font-mono text-slate-800 mt-0.5">{draftPacketStats.totalStudents}</p></div>
                <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-inner text-center"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Leftover Student Flags</p><p className="text-xl font-bold font-mono text-indigo-600 mt-0.5">{selectedStudents.length}</p></div>
                <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-inner text-center"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Available Teachers</p><p className="text-xl font-bold font-mono text-emerald-600 mt-0.5">{evaluators.length}</p></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                
                {/* Stage Controls Block */}
                <div className="rounded-xl border border-slate-200 p-5 bg-white shadow-sm space-y-4 lg:col-span-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Pick the Active Exam Round Phase</label>
                    <select value={evaluationRound.roundType} onChange={(e) => setEvaluationRound({ ...evaluationRound, roundType: e.target.value, roundName: e.target.value.replace('_', ' ').toUpperCase() })} className="w-full border border-slate-200 rounded-lg p-2 bg-white text-xs font-medium focus:outline-none text-slate-700">
                      <option value="first_evaluation">First Evaluation Marking Phase</option>
                      <option value="second_evaluation">Second Evaluation Marking Phase</option>
                      <option value="revaluation">Revaluation Checking Phase</option>
                    </select>
                  </div>
                  
                  <div className="bg-slate-50 border rounded-xl p-4 space-y-2 text-xs text-slate-500 font-medium leading-relaxed">
                    <p className="flex justify-between border-b pb-1.5 border-slate-200"><span>Draft bundles being grouped:</span> <strong className="text-slate-800 font-bold">{packets.length} bundles ready</strong></p>
                    <p className="flex justify-between border-b pb-1.5 border-slate-200"><span>Student records matching:</span> <strong className="text-slate-800 font-bold">{students.length} entries</strong></p>
                    <p className="flex justify-between"><span>Permitted active teachers list:</span> <strong className="text-slate-800 font-bold">{evaluators.length} staff found</strong></p>
                  </div>
                  
                  <div className="flex flex-col gap-2 pt-2">
                    <button onClick={addPacket} disabled={selectedStudents.length === 0} className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white rounded-xl text-xs font-bold transition-all shadow-sm">
                      Add Current Selected Students To a Draft Box
                    </button>
                    <button onClick={handleCreatePackets} disabled={loading || packets.length === 0} className="w-full py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-xl text-xs font-bold transition-all shadow-sm">
                      {loading ? 'Saving bundle data to data registry...' : 'Save All Staged Bundles Permanently'}
                    </button>
                  </div>
                </div>

                {/* Staging Room Preview Frame Container */}
                <div className="rounded-xl border border-slate-200 p-5 bg-white shadow-sm lg:col-span-7 space-y-3">
                  <div className="flex items-center justify-between border-b pb-2">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Unsaved Staged Bundles Preview List</h3>
                    <span className="text-xs font-mono font-bold text-purple-700">Draft Boxes: {draftPacketSummaries.length} items</span>
                  </div>
                  
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                    {draftPacketSummaries.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-xs text-slate-400 font-medium">
                        Staging dashboard room is empty. Select students from the main interface page and select "Create Bundle" to group them into a box layout here.
                      </div>
                    ) : (
                      draftPacketSummaries.map((packet, index) => (
                        <div key={packet.packetNumber} className="rounded-xl border border-slate-100 bg-slate-50 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                          <div className="min-w-0 space-y-1">
                            <p className="font-bold text-slate-800 text-sm">{packet.packetLabel}</p>
                            <p className="text-[11px] text-slate-400 font-semibold">{packet.totalStudents} answer scripts linked · Evaluation layer: {evaluationRound.roundName}</p>
                            <p className="text-[10px] font-medium text-slate-500 bg-white p-1 rounded border border-slate-100 max-w-sm truncate">
                              Student samples inside: {packet.previewStudents.length > 0 ? packet.previewStudents.join(', ') : 'No names added to list grid layout yet'}
                            </p>
                          </div>
                          <button onClick={() => removePacket(index)} className="px-2.5 py-1.5 text-red-600 border border-red-200 hover:bg-red-50 font-bold bg-white rounded-lg transition-all text-[11px] self-end sm:self-center flex items-center gap-1">
                            <Trash2 className="w-3 h-3" /> Discard
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

              </div>
            </div>
            <div className="border-t border-slate-100 px-6 py-3.5 bg-slate-50 flex justify-end shadow-inner"><button onClick={() => setShowCreateModal(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-xs font-bold transition-colors">Exit Review Room Window</button></div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PacketManager;